import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import ParentLayout from '../components/ParentLayout';
import { Calendar, Clock, MapPin, Save, X, AlertCircle, ImagePlus, Trash2, Paperclip, FileText, HandHeart, ExternalLink, Copy } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { formatDatePartET, formatTimePartET, combineDateTimeForServer } from '../utils/tz';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EVENT_TYPES = [
  { value: 'practice', label: 'Practice' },
  { value: 'game', label: 'Game' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'fundraiser', label: 'Fundraiser' },
  { value: 'community', label: 'Community Event' },
  { value: 'other', label: 'Other' }
];

// Mirrors backend _PLAYER_RSVP_EVENT_TYPES in routes_step4_5.py. These
// types always force rsvp_required=true and attendance_enabled=true; the
// "Mandatory" checkbox is locked-on for them so the UI matches the rule.
const PLAYER_RSVP_EVENT_TYPES = new Set([
  'practice', 'game', 'scrimmage', 'equipment_day', 'photo_day',
]);

// Mirrors backend VolunteerRoleType enum in models_step7.py. Kept in
// lockstep so the optional "Create linked volunteer opportunity" sub-form
// emits valid role_type values to POST /api/volunteer/opportunities.
// Values MUST match backend Pydantic enum VolunteerRoleType exactly
// (see backend/models_step7.py). User-facing labels are intentionally
// friendlier than the enum keys; only the submitted `value` is
// validated against the backend enum.
const VOLUNTEER_ROLE_TYPES = [
  { value: 'general', label: 'General' },
  { value: 'chain_crew', label: 'Chain Crew' },
  { value: 'fundraiser_support', label: 'Fundraiser Support' },
  { value: 'snack_table', label: 'Snack Stand' },
  { value: 'cleanup', label: 'Cleanup' },
  { value: 'photography', label: 'Photographer' },
  { value: 'equipment_day_support', label: 'Equipment' },
];

// Roles allowed to create volunteer opportunities (mirrors the gate on
// POST /api/volunteer/opportunities). team_staff / team_mom can create
// events but not volunteer opportunities, so they don't see the checkbox.
const VOLUNTEER_CREATOR_ROLES = new Set(['super_admin', 'league_admin', 'coach']);

export default function AdminEventCreatePage() {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const sourceEventId = searchParams.get('source') || null;
  const isEditMode = !!eventId;
  // Duplicate mode: reachable via /admin/events/new?source=<event_id>. Prefills
  // the form from an existing event but creates a NEW event on save. Attendance
  // records, RSVPs, sent-reminder history, and audit logs are keyed on
  // event_id — a fresh event_id makes them non-attach by design, so we do NOT
  // need to filter any of that out server-side.
  const isDuplicateMode = !isEditMode && !!sourceEventId;
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [fetchingEvent, setFetchingEvent] = useState(isEditMode || isDuplicateMode);
  const [error, setError] = useState(null);
  const [duplicateNotice, setDuplicateNotice] = useState('');
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [guestSearch, setGuestSearch] = useState('');
  const [showGuestPicker, setShowGuestPicker] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  // Optional volunteer-linker state (Phase: event-create checkbox flow).
  // create_volunteer_opportunity controls the optional checkbox; the
  // sub-form below only renders when it is true. linkedOpportunityExists
  // is detected in edit mode to swap the checkbox for a read-only notice.
  const [linkedOpportunityExists, setLinkedOpportunityExists] = useState(false);
  const [linkedOpportunityTitle, setLinkedOpportunityTitle] = useState('');
  const [volunteerWarning, setVolunteerWarning] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    event_type: 'practice',
    description: '',
    date: '',
    start_time: '',
    end_time: '',
    location: '',
    address: '',
    team_id: '',
    invited_player_ids: [],
    is_mandatory: false,
    notes: '',
    image_url: '',
    image_file_key: '',
    attachment_url: '',
    attachment_file_key: '',
    attachment_filename: '',
    // Optional volunteer-link sub-form. Defaults match the safest mapping
    // we agreed on: 1 slot, role=general. Admins can edit on the
    // Volunteer page later. One-shot create only; never auto-syncs on
    // future event edits.
    create_volunteer_opportunity: false,
    // Aug 2026: repeatable list of {role_type, num_slots} pairs so an
    // event can spawn multiple linked volunteer opportunities in one
    // pass (Chain Crew + Snack Stand + Equipment, etc). Backend schema
    // is unchanged — we simply POST N times to /api/volunteer/opportunities,
    // all sharing the same event_id.
    volunteer_needs: [{ role_type: 'general', num_slots: 1 }]
  });

  useEffect(() => {
    fetchTeams();
    fetchPlayers();
    if (isEditMode) {
      fetchEvent();
      fetchLinkedOpportunity();
    } else if (isDuplicateMode) {
      fetchSourceForDuplicate();
    }
  }, [eventId, sourceEventId]);

  // Player-RSVP event types must always carry rsvp_required + attendance_enabled.
  // Force is_mandatory ON whenever the type is in the player set. Other types
  // keep whatever the admin chose.
  useEffect(() => {
    if (PLAYER_RSVP_EVENT_TYPES.has(formData.event_type) && !formData.is_mandatory) {
      setFormData(prev => ({ ...prev, is_mandatory: true }));
    }
  }, [formData.event_type, formData.is_mandatory]);

  const fetchTeams = async () => {
    try {
      // RBAC (Feb 2026): coach / team_staff / team_mom only see teams
      // they are actually assigned to. Admins keep the full org list.
      const role = user?.role;
      const scopedRoles = ['coach', 'team_staff', 'team_mom'];
      if (role && scopedRoles.includes(role)) {
        const res = await axios.get(`${API_URL}/api/coach/my-teams`, { withCredentials: true });
        const arr = Array.isArray(res.data) ? res.data : (res.data?.teams || []);
        setTeams(arr);
      } else {
        const res = await axios.get(`${API_URL}/api/teams`, { withCredentials: true });
        setTeams(res.data.teams || res.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch teams:', err);
    }
  };

  const fetchPlayers = async () => {
    try {
      // Admins need to see every active player in the league so they can
      // build Custom Guest Lists across teams (e.g. a one-player SMS
      // test event). Non-admins keep the household-scoped view returned
      // by /api/players — coaches/team staff/team moms don't build
      // cross-team custom rosters from this picker.
      const isAdmin = user?.role === 'super_admin' || user?.role === 'league_admin';
      const url = isAdmin
        ? `${API_URL}/api/admin/players/all`
        : `${API_URL}/api/players`;
      const res = await axios.get(url, { withCredentials: true });
      const arr = Array.isArray(res.data) ? res.data : (res.data?.players || []);
      // Active non-archived only (admin endpoint filters server-side; the
      // parent endpoint returns only own-household players, which are
      // already non-archived — this filter is a defensive no-op there).
      setPlayers(arr.filter(p => !p.archived));
    } catch (err) {
      console.error('Failed to fetch players:', err);
    }
  };

  // In edit mode: detect any existing volunteer opportunity already
  // linked to this event so we can swap the optional checkbox for a
  // read-only notice. Silent no-op on failure (e.g. event has no link).
  const fetchLinkedOpportunity = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/events/${eventId}/volunteer-opportunities`,
        { withCredentials: true },
      );
      const opps = Array.isArray(res.data) ? res.data : [];
      if (opps.length > 0) {
        setLinkedOpportunityExists(true);
        setLinkedOpportunityTitle(opps[0].title || '');
      }
    } catch (err) {
      // 404 (no event yet) or 401 — silently leave checkbox available.
    }
  };

  const fetchEvent = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/events/${eventId}`, { withCredentials: true });
      const ev = res.data;
      // ET-safe: backend stores UTC; render in America/New_York so the
      // form always shows the wall-clock time an admin actually meant
      // regardless of the browser timezone (Feb 2026 fix).
      setFormData({
        title: ev.title || '',
        event_type: ev.event_type || 'practice',
        description: ev.description || '',
        date: formatDatePartET(ev.start_time),
        start_time: formatTimePartET(ev.start_time),
        end_time: formatTimePartET(ev.end_time),
        location: ev.location_name || '',
        address: ev.address || '',
        team_id: ev.team_id || '',
        invited_player_ids: Array.isArray(ev.invited_player_ids) ? ev.invited_player_ids : [],
        is_mandatory: ev.rsvp_required || ev.attendance_enabled || false,
        notes: ev.notes || '',
        image_url: ev.image_url || '',
        image_file_key: ev.image_file_key || ''
      });
      if (ev.image_url) setImagePreview(ev.image_url);
    } catch (err) {
      setError('Failed to load event');
    } finally {
      setFetchingEvent(false);
    }
  };

  // Duplicate mode: fetch the source event and prefill the form. Title is
  // prefixed with "Copy of " and date/start/end are intentionally blanked
  // so the admin MUST pick a new date/time before saving. Attendance /
  // RSVP / reminder history are event_id-keyed and don't need scrubbing.
  const fetchSourceForDuplicate = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/events/${sourceEventId}`,
        { withCredentials: true }
      );
      const ev = res.data;
      const srcTitle = ev.title || '';
      setFormData((prev) => ({
        ...prev,
        title: srcTitle.startsWith('Copy of ') ? srcTitle : `Copy of ${srcTitle}`,
        event_type: ev.event_type || 'practice',
        description: ev.description || '',
        date: '',
        start_time: '',
        end_time: '',
        location: ev.location_name || '',
        address: ev.address || '',
        team_id: ev.team_id || '',
        invited_player_ids: Array.isArray(ev.invited_player_ids) ? ev.invited_player_ids : [],
        is_mandatory: ev.rsvp_required || ev.attendance_enabled || false,
        notes: ev.notes || '',
        // Reuse the same image; the file_key stays valid across events.
        image_url: ev.image_url || '',
        image_file_key: ev.image_file_key || '',
      }));
      if (ev.image_url) setImagePreview(ev.image_url);
      setDuplicateNotice(
        `Duplicating from "${srcTitle}". Pick a new date and time before saving.`
      );
    } catch (err) {
      setError('Failed to load source activity for duplication.');
    } finally {
      setFetchingEvent(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be under 5MB');
      return;
    }

    setUploadingImage(true);
    setError(null);

    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result;
        setImagePreview(base64);

        const res = await axios.post(`${API_URL}/api/events/upload-image`, {
          file_data: base64,
          filename: file.name,
          content_type: file.type
        }, { withCredentials: true });

        updateField('image_url', res.data.image_url);
        updateField('image_file_key', res.data.file_key);
        setUploadingImage(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setError('Failed to upload image');
      setUploadingImage(false);
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    updateField('image_url', '');
    updateField('image_file_key', '');
  };

  const handleAttachmentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Attachment must be under 5MB');
      return;
    }
    setUploadingAttachment(true);
    setError(null);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64 = reader.result;
          const res = await axios.post(`${API_URL}/api/events/upload-attachment`, {
            file_data: base64,
            filename: file.name,
            content_type: file.type || 'application/pdf',
          }, { withCredentials: true });
          updateField('attachment_url', res.data.attachment_url);
          updateField('attachment_file_key', res.data.file_key);
          updateField('attachment_filename', res.data.filename || file.name);
        } catch (err) {
          setError(err.response?.data?.detail || 'Failed to upload attachment');
        } finally {
          setUploadingAttachment(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setError('Failed to upload attachment');
      setUploadingAttachment(false);
    }
  };

  const removeAttachment = () => {
    updateField('attachment_url', '');
    updateField('attachment_file_key', '');
    updateField('attachment_filename', '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Naive ISO — backend interprets these as America/New_York local
      // time and stores as UTC (see backend tz_utils.parse_admin_datetime).
      const startDateTime = combineDateTimeForServer(formData.date, formData.start_time);
      const endDateTime = formData.end_time ? combineDateTimeForServer(formData.date, formData.end_time) : null;

      const payload = {
        title: formData.title,
        event_type: formData.event_type,
        description: formData.description || null,
        start_time: startDateTime,
        end_time: endDateTime,
        location_name: isEditMode ? (formData.location || null) : undefined,
        location: !isEditMode ? (formData.location || null) : undefined,
        address: formData.address || null,
        team_id: formData.team_id || null,
        invited_player_ids: formData.invited_player_ids,
        rsvp_required: formData.is_mandatory,
        attendance_enabled: formData.is_mandatory,
        notes: formData.notes || null,
        image_url: formData.image_url || null,
        image_file_key: formData.image_file_key || null,
        attachment_url: formData.attachment_url || null,
        attachment_file_key: formData.attachment_file_key || null,
        attachment_filename: formData.attachment_filename || null
      };

      // Remove undefined keys
      Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);

      let savedEventId = eventId;
      if (isEditMode) {
        await axios.put(`${API_URL}/api/events/${eventId}`, payload, { withCredentials: true });
      } else {
        const createRes = await axios.post(`${API_URL}/api/events`, payload, { withCredentials: true });
        // Backend returns { event: {...}, events_created: N }
        savedEventId = createRes.data?.event?.event_id || createRes.data?.event_id || null;
      }

      // Optional "Create linked volunteer opportunity" sub-form.
      // Aug 2026: repeatable — issues one existing POST per unique
      // (role_type) after merging any duplicate rows by summing slots.
      // All resulting opportunities share the same event_id.
      // Partial failure is surfaced non-destructively: the event stays
      // saved, successfully-created opportunities are preserved, and the
      // admin sees which roles failed.
      const userRole = user?.role;
      const canCreateVolunteer = userRole && VOLUNTEER_CREATOR_ROLES.has(userRole);
      if (
        formData.create_volunteer_opportunity
        && canCreateVolunteer
        && !linkedOpportunityExists
        && savedEventId
      ) {
        // Merge duplicates by role_type, sum slots, floor at 1.
        const merged = new Map();
        for (const row of (formData.volunteer_needs || [])) {
          const rt = (row?.role_type || 'general').toString();
          const slots = Math.max(1, parseInt(row?.num_slots, 10) || 1);
          merged.set(rt, (merged.get(rt) || 0) + slots);
        }
        const uniqueNeeds = Array.from(merged.entries()).map(([role_type, num_slots]) => ({
          role_type, num_slots,
        }));

        const results = [];
        for (const need of uniqueNeeds) {
          const roleLabel = (
            VOLUNTEER_ROLE_TYPES.find(rt => rt.value === need.role_type)?.label
            || need.role_type
          );
          // Aug 2026 — volunteer opportunities are intentionally decoupled
          // from event audience. Even when the parent event is restricted
          // to a team or custom guest list, the linked volunteer
          // opportunity must remain open to all eligible volunteers
          // across the organization. `team_id=null` + `org_wide=true`
          // makes GET /api/volunteer/opportunities return it to every
          // signed-in user (see routes_step7.py get_volunteer_opportunities).
          const vp = {
            title: `${formData.title} — ${roleLabel}`,
            description: formData.description || '',
            role_type: need.role_type,
            event_id: savedEventId,
            team_id: null,
            org_wide: true,
            date: formData.date,
            start_time: formData.start_time,
            end_time: formData.end_time || formData.start_time,
            location: formData.location || '',
            num_slots: need.num_slots,
            instructions: '',
            contact_person: '',
            reminder_enabled: false,
          };
          try {
            await axios.post(`${API_URL}/api/volunteer/opportunities`, vp, { withCredentials: true });
            results.push({ role: roleLabel, slots: need.num_slots, ok: true });
          } catch (volErr) {
            results.push({
              role: roleLabel,
              slots: need.num_slots,
              ok: false,
              detail: volErr?.response?.data?.detail || 'Create failed',
            });
          }
        }

        const failed = results.filter(r => !r.ok);
        if (failed.length > 0) {
          const okList = results
            .filter(r => r.ok)
            .map(r => `${r.role} (${r.slots})`)
            .join(', ') || 'none';
          const failList = failed
            .map(r => `${r.role} (${r.slots}) — ${r.detail}`)
            .join('; ');
          setVolunteerWarning(
            `Event saved. Volunteer roles created: ${okList}. Failed: ${failList}. `
            + 'You can add the failed roles from the Volunteer page.'
          );
          setLoading(false);
          return;
        }
      }

      navigate('/schedule');
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to ${isEditMode ? 'update' : 'create'} event`);
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (fetchingEvent) {
    return (
      <ParentLayout>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="h-96 bg-gray-200 rounded-xl"></div>
          </div>
        </div>
      </ParentLayout>
    );
  }

  return (
    <ParentLayout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="event-form-page">
        {/* Header */}
        <div className="mb-8">
          <p className="text-gold text-sm font-bold tracking-widest uppercase mb-1">SCHEDULE</p>
          <h1 className="text-3xl md:text-4xl font-display text-navy uppercase tracking-wide" data-testid="event-form-title">
            {isEditMode ? 'EDIT EVENT' : (isDuplicateMode ? 'DUPLICATE EVENT' : 'CREATE EVENT')}
          </h1>
        </div>

        {duplicateNotice && (
          <div
            className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3"
            data-testid="duplicate-notice"
          >
            <Copy className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
            <p className="text-blue-900 text-sm">{duplicateNotice}</p>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3" data-testid="event-form-error">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6" data-testid="event-form">
          <div className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Event Title *</label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => updateField('title', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent"
                placeholder="e.g., Team Practice, Game vs. Lions"
                data-testid="event-title-input"
              />
            </div>

            {/* Event Type & Team */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Event Type *</label>
                <select
                  value={formData.event_type}
                  onChange={(e) => updateField('event_type', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent"
                  data-testid="event-type-select"
                >
                  {EVENT_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Team {['coach','team_staff','team_mom'].includes(user?.role) ? '(required — assigned team only)' : '(optional)'}</label>
                <select
                  value={formData.team_id}
                  onChange={(e) => updateField('team_id', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent"
                  data-testid="event-team-select"
                >
                  {/* RBAC (Feb 2026): coach / team_staff / team_mom cannot
                      create league-wide events, so the "All Teams" option
                      is hidden for those roles. Admins keep it. */}
                  {!['coach','team_staff','team_mom'].includes(user?.role) && (
                    <option value="">All Teams / League-wide</option>
                  )}
                  {['coach','team_staff','team_mom'].includes(user?.role) && (
                    <option value="" disabled>Select an assigned team…</option>
                  )}
                  {teams.map(team => (
                    <option key={team.team_id} value={team.team_id}>{team.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Custom Guest List (optional, overrides team/league when populated) */}
            <div data-testid="event-guest-picker">
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <label className="block text-sm font-medium text-gray-700">
                  Custom Guest List (optional)
                </label>
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                    formData.invited_player_ids.length > 0
                      ? 'bg-navy text-white'
                      : 'bg-gray-100 text-gray-500'
                  }`}
                  data-testid="event-guest-count"
                >
                  {formData.invited_player_ids.length} / {players.length} selected
                </span>
              </div>
              <p className="text-xs text-gray-500 mb-2">
                Leave empty to use the team/league audience above.
                Selecting players here overrides the team selection.
                Each selected player's household (parents/guardians) is automatically
                included for RSVPs and reminders.
              </p>

              {/* Currently-invited chips — visible at all times when non-empty */}
              {formData.invited_player_ids.length > 0 && (
                <div
                  className="mb-2 max-h-32 overflow-y-auto p-2 bg-navy/5 border border-navy/10 rounded-lg flex flex-wrap gap-1.5"
                  data-testid="event-guest-chips"
                >
                  {formData.invited_player_ids.map(pid => {
                    const p = players.find(x => x.player_id === pid);
                    const label = p ? `${p.first_name || ''} ${p.last_name || ''}`.trim() : pid;
                    return (
                      <span
                        key={pid}
                        className="inline-flex items-center gap-1 bg-white border border-navy/20 text-navy text-xs px-2 py-1 rounded-full"
                        data-testid={`event-guest-chip-${pid}`}
                      >
                        {label || pid}
                        <button
                          type="button"
                          onClick={() => updateField(
                            'invited_player_ids',
                            formData.invited_player_ids.filter(id => id !== pid)
                          )}
                          className="ml-1 text-navy/60 hover:text-red"
                          aria-label={`Remove ${label}`}
                          data-testid={`event-guest-chip-remove-${pid}`}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2 mb-2">
                <button
                  type="button"
                  onClick={() => setShowGuestPicker(s => !s)}
                  className="px-3 py-2 text-sm border border-navy/30 text-navy rounded-lg hover:bg-navy/5"
                  data-testid="event-guest-picker-toggle"
                >
                  {showGuestPicker ? 'Hide players' : 'Choose players'}
                </button>
                {formData.invited_player_ids.length > 0 && (
                  <button
                    type="button"
                    onClick={() => updateField('invited_player_ids', [])}
                    className="text-xs text-red hover:underline"
                    data-testid="event-guest-clear"
                  >
                    Clear all
                  </button>
                )}
              </div>
              {showGuestPicker && (
                <div className="border border-gray-200 rounded-lg p-3 max-h-64 overflow-y-auto bg-gray-50">
                  <input
                    type="text"
                    placeholder="Search by player or team name…"
                    value={guestSearch}
                    onChange={(e) => setGuestSearch(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded mb-3 focus:ring-2 focus:ring-navy"
                    data-testid="event-guest-search"
                  />
                  {(() => {
                    const teamNameById = Object.fromEntries(teams.map(t => [t.team_id, t.name]));
                    const term = guestSearch.trim().toLowerCase();
                    const filtered = players.filter(p => {
                      if (!term) return true;
                      const teamName = teamNameById[p.team_id] || 'Unassigned';
                      const haystack = `${p.first_name || ''} ${p.last_name || ''} ${teamName}`.toLowerCase();
                      return haystack.includes(term);
                    });
                    if (filtered.length === 0) {
                      return <p className="text-xs text-gray-500 py-2">No players match.</p>;
                    }
                    // Group by team_name
                    const groups = {};
                    filtered.forEach(p => {
                      const key = teamNameById[p.team_id] || 'Unassigned';
                      (groups[key] ||= []).push(p);
                    });
                    const order = Object.keys(groups).sort((a, b) => {
                      if (a === 'Unassigned') return 1;
                      if (b === 'Unassigned') return -1;
                      return a.localeCompare(b);
                    });
                    return order.map(group => (
                      <div key={group} className="mb-3">
                        <p className="text-xs font-semibold text-navy mb-1">{group}</p>
                        <div className="space-y-1">
                          {groups[group].map(p => {
                            const checked = formData.invited_player_ids.includes(p.player_id);
                            return (
                              <label
                                key={p.player_id}
                                className={`flex items-center gap-2 text-sm cursor-pointer px-2 py-1 rounded ${
                                  checked ? 'bg-navy/5 hover:bg-navy/10' : 'hover:bg-white'
                                }`}
                                data-testid={`event-guest-option-${p.player_id}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => {
                                    const next = e.target.checked
                                      ? [...formData.invited_player_ids, p.player_id]
                                      : formData.invited_player_ids.filter(id => id !== p.player_id);
                                    updateField('invited_player_ids', next);
                                  }}
                                  className="w-4 h-4"
                                />
                                <span>{p.first_name} {p.last_name}</span>
                                {p.age_group && (
                                  <span className="text-xs text-gray-400">({p.age_group})</span>
                                )}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" /> Date *
                </label>
                <input
                  type="date"
                  required
                  value={formData.date}
                  onChange={(e) => updateField('date', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent"
                  data-testid="event-date-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="w-4 h-4 inline mr-1" /> Start Time *
                </label>
                <input
                  type="time"
                  required
                  value={formData.start_time}
                  onChange={(e) => updateField('start_time', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent"
                  data-testid="event-start-time-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="w-4 h-4 inline mr-1" /> End Time
                </label>
                <input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => updateField('end_time', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent"
                  data-testid="event-end-time-input"
                />
              </div>
            </div>

            {/* Location */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="w-4 h-4 inline mr-1" /> Location Name
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => updateField('location', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent"
                  placeholder="e.g., Chiefs Field, Community Center"
                  data-testid="event-location-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => updateField('address', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent"
                  placeholder="123 Main St, Park City, UT"
                  data-testid="event-address-input"
                />
              </div>
            </div>

            {/* Image / Flyer Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <ImagePlus className="w-4 h-4 inline mr-1" /> Event Flyer / Image
              </label>
              {imagePreview ? (
                <div className="relative rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                  <img
                    src={imagePreview}
                    alt="Event flyer"
                    className="w-full max-h-64 object-contain"
                    data-testid="event-image-preview"
                  />
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute top-2 right-2 bg-red text-white p-1.5 rounded-full hover:bg-red/80 transition"
                    data-testid="event-remove-image"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label
                  className={`flex flex-col items-center justify-center w-full h-36 border-2 border-dashed rounded-lg cursor-pointer transition ${
                    uploadingImage ? 'border-gold bg-gold/5' : 'border-gray-300 hover:border-navy hover:bg-navy/5'
                  }`}
                  data-testid="event-image-upload"
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploadingImage}
                  />
                  {uploadingImage ? (
                    <div className="text-center">
                      <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                      <p className="text-sm text-gray-500">Uploading...</p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <ImagePlus className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">Click to upload a flyer or image</p>
                      <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 5MB</p>
                    </div>
                  )}
                </label>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => updateField('description', e.target.value)}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent"
                placeholder="Additional details about the event..."
                data-testid="event-description-input"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Internal Notes (admin only)</label>
              <textarea
                value={formData.notes}
                onChange={(e) => updateField('notes', e.target.value)}
                rows={2}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent"
                placeholder="Notes visible only to admins..."
                data-testid="event-notes-input"
              />
            </div>

            {/* Mandatory Toggle */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="mandatory"
                checked={formData.is_mandatory}
                disabled={PLAYER_RSVP_EVENT_TYPES.has(formData.event_type)}
                onChange={(e) => updateField('is_mandatory', e.target.checked)}
                className="w-5 h-5 rounded border-gray-300 text-red focus:ring-red mt-0.5 disabled:opacity-60"
                data-testid="event-mandatory-checkbox"
              />
              <div>
                <label htmlFor="mandatory" className="text-sm font-medium text-gray-700">
                  Mark as mandatory attendance
                </label>
                {PLAYER_RSVP_EVENT_TYPES.has(formData.event_type) && (
                  <p
                    className="text-xs text-gray-500 mt-1"
                    data-testid="event-mandatory-helper"
                  >
                    Practices, games, camps, scrimmages, tournaments,
                    team photos, and equipment days always enable RSVP and
                    attendance tracking.
                  </p>
                )}
              </div>
            </div>

            {/* Optional: Linked Volunteer Opportunity.
                One-shot create. In edit mode, if a linked opportunity
                already exists, swap the checkbox for a read-only notice
                with a click-through to the Volunteer page.
                Hidden entirely for team_staff / team_mom because the
                backend rejects their POST /api/volunteer/opportunities. */}
            {isEditMode && linkedOpportunityExists ? (
              <div
                className="flex items-start gap-3 p-3 rounded-lg bg-navy/5 border border-navy/20"
                data-testid="event-linked-volunteer-notice"
              >
                <HandHeart className="w-5 h-5 text-navy mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-navy">
                    Volunteer opportunity already linked
                  </p>
                  {linkedOpportunityTitle && (
                    <p className="text-xs text-gray-600 mt-0.5 truncate">
                      {linkedOpportunityTitle}
                    </p>
                  )}
                  <Link
                    to="/admin/volunteers"
                    className="text-xs text-navy hover:underline inline-flex items-center mt-1"
                    data-testid="event-linked-volunteer-manage-link"
                  >
                    Manage on Volunteer page
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </Link>
                </div>
              </div>
            ) : user?.role && VOLUNTEER_CREATOR_ROLES.has(user.role) ? (
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="create-volunteer-opportunity"
                    checked={formData.create_volunteer_opportunity}
                    onChange={(e) => updateField('create_volunteer_opportunity', e.target.checked)}
                    className="w-5 h-5 rounded border-gray-300 text-navy focus:ring-navy mt-0.5"
                    data-testid="event-create-volunteer-checkbox"
                  />
                  <div>
                    <label htmlFor="create-volunteer-opportunity" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <HandHeart className="w-4 h-4 text-navy" />
                      Create linked volunteer opportunity
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Optional. Creates a volunteer opportunity using this
                      event&apos;s date, time, and location. You can fine-tune
                      the details on the Volunteer page afterward.
                    </p>
                  </div>
                </div>

                {formData.create_volunteer_opportunity && (
                  <div
                    className="ml-8 space-y-2"
                    data-testid="event-volunteer-subform"
                  >
                    <div className="flex items-center justify-between text-xs font-semibold text-gray-600">
                      <span>Volunteer Needs</span>
                    </div>
                    <div className="grid grid-cols-[1fr_120px_auto] gap-2 text-xs text-gray-500 px-1">
                      <span>Role Type</span>
                      <span># of Slots</span>
                      <span></span>
                    </div>
                    {(formData.volunteer_needs || []).map((row, idx) => (
                      <div
                        key={idx}
                        className="grid grid-cols-[1fr_120px_auto] gap-2 items-center"
                        data-testid={`event-volunteer-need-row-${idx}`}
                      >
                        <select
                          value={row.role_type}
                          onChange={(e) => {
                            const next = [...formData.volunteer_needs];
                            next[idx] = { ...next[idx], role_type: e.target.value };
                            updateField('volunteer_needs', next);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-navy focus:border-transparent"
                          data-testid={`event-volunteer-role-type-select-${idx}`}
                        >
                          {VOLUNTEER_ROLE_TYPES.map(rt => (
                            <option key={rt.value} value={rt.value}>{rt.label}</option>
                          ))}
                        </select>
                        <input
                          type="number"
                          min="1"
                          value={row.num_slots}
                          onChange={(e) => {
                            const next = [...formData.volunteer_needs];
                            next[idx] = { ...next[idx], num_slots: e.target.value };
                            updateField('volunteer_needs', next);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-navy focus:border-transparent"
                          data-testid={`event-volunteer-num-slots-input-${idx}`}
                        />
                        {idx > 0 ? (
                          <button
                            type="button"
                            onClick={() => {
                              const next = formData.volunteer_needs.filter((_, i) => i !== idx);
                              updateField('volunteer_needs', next);
                            }}
                            className="text-xs font-semibold text-red-600 hover:underline px-2"
                            data-testid={`event-volunteer-remove-row-${idx}`}
                          >
                            Remove
                          </button>
                        ) : (
                          <span className="w-[64px]" />
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        updateField(
                          'volunteer_needs',
                          [...(formData.volunteer_needs || []), { role_type: 'general', num_slots: 1 }],
                        );
                      }}
                      className="text-sm font-semibold text-navy hover:underline pt-1"
                      data-testid="event-volunteer-add-row-btn"
                    >
                      + Add Another Role
                    </button>
                  </div>
                )}
              </div>
            ) : null}

            {volunteerWarning && (
              <div
                className="flex items-start gap-3 p-3 rounded-lg bg-yellow-50 border border-yellow-200"
                data-testid="event-volunteer-warning"
              >
                <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm text-yellow-800">{volunteerWarning}</p>
                  <button
                    type="button"
                    onClick={() => { setVolunteerWarning(''); navigate('/schedule'); }}
                    className="text-xs text-navy hover:underline mt-1"
                  >
                    Continue to Schedule
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-4 mt-8 pt-6 border-t border-gray-100">
            <button
              type="button"
              onClick={() => navigate(isEditMode ? `/events/${eventId}` : '/schedule')}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
              data-testid="event-cancel-button"
            >
              <X className="w-4 h-4 inline mr-2" />
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || uploadingImage}
              className="flex-1 px-6 py-3 bg-red text-white rounded-lg hover:bg-red/90 transition font-medium disabled:opacity-50"
              data-testid="event-submit-button"
            >
              <Save className="w-4 h-4 inline mr-2" />
              {loading ? (isEditMode ? 'Saving...' : 'Creating...') : (isEditMode ? 'Save Changes' : 'Create Event')}
            </button>
          </div>
        </form>
      </div>
    </ParentLayout>
  );
}
